/**
 * Access Document Credential Form
 *
 * Form for configuring Access Document credentials with:
 * - Instance ID and Shared Secret for HMAC-SHA256 authentication
 * - Optional custom web service URL
 * - Test connection functionality
 */

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export interface AccessDocumentFormData {
  instanceId: string;
  sharedSecret: string;
  webServiceUrl: string;
}

interface AccessDocumentCredentialFormProps {
  formData: AccessDocumentFormData;
  onChange: (data: Partial<AccessDocumentFormData>) => void;
  isEditing?: boolean;
  testResult?: { success: boolean; message: string; data?: any; details?: any } | null;
  testing?: boolean;
  onTest?: () => Promise<void>;
}

export function AccessDocumentCredentialForm({
  formData,
  onChange,
  isEditing = false,
  testResult,
  testing,
  onTest,
}: AccessDocumentCredentialFormProps) {
  const handleChange = (field: keyof AccessDocumentFormData, value: string) => {
    onChange({ [field]: value });
  };

  return (
    <div className="space-y-4">
      {/* Instance ID */}
      <div className="space-y-2">
        <Label htmlFor="instanceId" className="text-sm font-medium">
          Instance ID <span className="text-red-500">*</span>
        </Label>
        <Input
          id="instanceId"
          placeholder="2491c9858f66401b8a88fb25d4d2c3c2"
          value={formData.instanceId || ''}
          onChange={(e) => handleChange('instanceId', e.target.value)}
          className="font-mono text-sm"
        />
        <p className="text-xs text-muted-foreground">
          Your Access Document instance identifier (GUID format)
        </p>
      </div>

      {/* Shared Secret */}
      <div className="space-y-2">
        <Label htmlFor="sharedSecret" className="text-sm font-medium">
          Shared Secret <span className="text-red-500">*</span>
        </Label>
        <Input
          id="sharedSecret"
          type="password"
          placeholder={isEditing ? "Leave unchanged or enter new secret" : "Enter shared secret"}
          value={formData.sharedSecret || ''}
          onChange={(e) => handleChange('sharedSecret', e.target.value)}
          autoComplete="new-password"
          className="font-mono text-sm"
        />
        <p className="text-xs text-muted-foreground">
          The HMAC shared secret for API authentication
        </p>
      </div>

      {/* Web Service URL (Optional) */}
      <div className="space-y-2">
        <Label htmlFor="webServiceUrl" className="text-sm font-medium">
          Web Service URL <span className="text-muted-foreground text-xs">(Optional)</span>
        </Label>
        <Input
          id="webServiceUrl"
          placeholder="https://documentws.accessacloud.com/v3.10.00/ADMWebService/api/1.0"
          value={formData.webServiceUrl || ''}
          onChange={(e) => handleChange('webServiceUrl', e.target.value)}
          className="font-mono text-sm"
        />
        <p className="text-xs text-muted-foreground">
          Leave empty to use the default Access Document URL. Only change for on-premise deployments.
        </p>
      </div>

    </div>
  );
}

export default AccessDocumentCredentialForm;
